import Fuse from 'fuse.js';
import { ID, UserAction, UserActionState, UserSession } from '@app/types';
import { GetEntitlementValueFn } from '@app/Context/AuthContext';
import buildUniversalActions from './actionsBySection/universalActions';
import { buildContactActions } from './actionsBySection/contactActions';
import buildContactBatchActions from './actionsBySection/contactBatchActions';
import buildGroupActions from './actionsBySection/groupActions';
import { pluralize } from '@support/string';
import Collection from '@models/Collection';
import Group from '@models/Group';
import { Dispatch } from 'redux';
import { navigationItems } from '@layouts/PrimaryLayout/Navigation';
import { CogIcon } from '@components/Icons';
import { GroupOptionItem, MenuOption, NavOptionItem } from './Option';
import { MenuSection } from '.';
import { NavigateFunction } from 'react-router-dom';
import { buildArchiveActions } from './actionsBySection/archivedContactActions';
import { ArchiveViewState } from '@reducers/ui/archiveView';
import Contact from '@models/Contact';

export interface SearchableSection<T> {
  title: string;
  search: (term: string) => Array<T>;
  unfilteredOptions?: T[];
}

const createActionsSearch = (actions: Array<UserAction>) => {
  const searchableActions = new Fuse(actions, {
    keys: ['title', 'synonyms', 'keyboard'],
    threshold: 0.3,
  });

  return (term: string) =>
    searchableActions.search(term).map(({ item }) => item) as Array<UserAction>;
};

export const actionsListToSearchableSection = ([actions, title]: [Array<UserAction>, string]) => {
  if (actions.length == 0) {
    return null;
  }

  return { title, unfilteredOptions: actions, search: createActionsSearch(actions) };
};

const selectedCountString = (prefix: string, count: number): string => {
  if (!count) {
    return prefix;
  }

  return `${prefix} - ${count} ${pluralize(count, 'person')} Selected`;
};

const navOptions = navigationItems
  .concat([
    {
      IconComponent: CogIcon,
      text: 'Settings',
      to: '/settings',
      keyChar: ',',
    },
  ])
  .map((navOption) => ({ type: 'nav', ...navOption }));

const searchableNavOptions = new Fuse(navOptions, {
  keys: ['text', 'keyChar'],
  threshold: 0.3,
});

const archiveSectionTitle = (archiveView: ArchiveViewState): string => {
  if (archiveView?.selectedContactBatch.size > 1) {
    return `Archived People Actions - ${archiveView.selectedContactBatch.size} People Selected`;
  } else if (archiveView?.selectedContact) {
    return `Archived Person Actions - ${archiveView.selectedContact.displayName}`;
  } else {
    return 'Archived Person Actions';
  }
};

export function searchableActionSectionsForState(
  userActionState: UserActionState,
  dispatch: Dispatch,
  navigate: NavigateFunction,
  user: UserSession,
  authLogoutFunction: () => void,
  userIsAdmin: boolean,
  getEntitlementValue: GetEntitlementValueFn,
  contactIsRestricted: (contact: Contact) => boolean,
  getContact: (id: ID) => Contact | undefined,
  contactLimitExceeded: boolean,
) {
  const groupActions = buildGroupActions(
    userActionState,
    dispatch,
    navigate,
    userIsAdmin,
    getEntitlementValue,
    contactIsRestricted,
    getContact,
    true,
  );
  const archiveActions = buildArchiveActions(userActionState.archiveView, dispatch);
  const contactActions = buildContactActions(
    userActionState,
    dispatch,
    navigate,
    contactIsRestricted,
    getEntitlementValue,
  );
  const contactBatchActions = buildContactBatchActions(
    userActionState,
    dispatch,
    contactIsRestricted,
    'Search Command Bar',
  );
  const universalActions = buildUniversalActions(
    dispatch,
    navigate,
    user,
    authLogoutFunction,
    contactLimitExceeded,
  );

  return [
    [groupActions, 'Group Actions'],
    [archiveActions, archiveSectionTitle(userActionState.archiveView)],
    [contactActions, `Person Actions - ${userActionState.selectedContact?.displayName}`],
    [
      contactBatchActions,
      selectedCountString('People Actions', userActionState.selectedContactBatch?.size),
    ],
    [universalActions, 'General Actions'],
  ]
    .map(actionsListToSearchableSection)
    .filter((x) => x);
}

export function searchableGroupSectionForCollection(
  groupCollection: Collection<Group>,
): SearchableSection<GroupOptionItem> {
  const trueGroups = groupCollection.items.valueSeq().toArray();
  const groupsForNav = trueGroups
    .slice(0, 1)
    .concat([new Group({ title: 'Shared People', slug: 'shared-with-me' })])
    .concat(trueGroups.slice(1))
    .map((group, idx) => {
      let keyboard = null;

      if (idx == 0) {
        keyboard = 's';
      } else if (idx == 1) {
        keyboard = 'w';
      } else if (idx < 11) {
        keyboard = String(idx - 1);
      }

      return { group, keyboard, type: 'group' };
    });

  const searchableGroups = new Fuse(groupsForNav, {
    keys: ['group.title', 'keyboard'],
    threshold: 0.3,
  });

  return {
    title: 'Groups',
    search: (term: string) =>
      searchableGroups.search(term).map(({ item }) => item) as Array<GroupOptionItem>,
  };
}

export const searchableNavSection = {
  title: 'Navigation',
  search: (term: string) =>
    searchableNavOptions.search(term).map(({ item }) => item) as Array<NavOptionItem>,
};

export function filterMenuSection(
  { title, unfilteredOptions, search }: SearchableSection<MenuOption>,
  term: string,
  defaultToUnfiltered = false,
): MenuSection {
  let filteredOptions = defaultToUnfiltered ? unfilteredOptions : null;

  if (!term && !defaultToUnfiltered) {
    return null;
  }

  if (term) {
    filteredOptions = search(term);
  }

  if (!filteredOptions || (filteredOptions && filteredOptions.length == 0)) {
    return null;
  }

  return {
    title,
    suggestions: filteredOptions,
  };
}
